import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from 'react';
import { supabase, ensureValidSession } from '../services/supabase';
import type { User, UserRole } from '../types';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  signup: (email: string, password: string, displayName?: string) => Promise<User>;
  login: (email: string, password: string) => Promise<User>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateEmail: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  isUserAdmin: () => boolean;
  setUserRole: (role: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const sessionRefreshInterval = useRef<NodeJS.Timeout | null>(null);

  // エラーログ関数
  const logError = (message: string, error?: any) => {
    console.error(`🔐 [AuthContext] ERROR: ${message}`, error || '');
  };

  // Supabaseユーザーからアプリケーションユーザーを作成
  const createUserFromSupabaseUser = async (supabaseUser: any, skipRoleFetch?: boolean): Promise<User> => {
    
    try {
      // 表示名の決定
      let displayName = supabaseUser.email || '';
      if (supabaseUser.user_metadata?.display_name) {
        displayName = supabaseUser.user_metadata.display_name;
      } else if (supabaseUser.user_metadata?.name) {
        displayName = supabaseUser.user_metadata.name;
      } else if (supabaseUser.email) {
        displayName = supabaseUser.email.split('@')[0];
      }

      // 既存のユーザーのロールを保持するか、DBから取得
      let role: UserRole = 'user';
      
      // ローカルストレージからロールを取得
      const storedRole = localStorage.getItem(`user_role_${supabaseUser.id}`);

      // 既存のユーザーと同じIDの場合、ロールを保持
      if ((currentUser && currentUser.id === supabaseUser.id && skipRoleFetch) || storedRole) {
        if (storedRole) {
          role = storedRole as UserRole;
        } else if (currentUser) {
          role = currentUser.role;
        }
      } else {
        // DBから実際のロールを取得（ブロックしないようにタイムアウト付き）
        try {
          // タイムアウト付きのPromise
          const fetchRoleWithTimeout = () => {
            return new Promise<UserRole>((resolve) => {
              // 2秒後にタイムアウト
              const timeoutId = setTimeout(() => {
                resolve('user');
              }, 2000);

              // データベースクエリ（Promiseとして扱う）
              (async () => {
                try {
                  const { data, error } = await supabase
                    .from('user_roles')
                    .select('role')
                    .eq('user_id', supabaseUser.id)
                    .maybeSingle();

                  clearTimeout(timeoutId);

                  if (!error && data) {
                    resolve(data.role as UserRole);
                  } else if (error && error.code === 'PGRST116') {
                    resolve('user');
                  } else if (error) {
                    logError('Role fetch error:', error);
                    resolve('user');
                  } else {
                    resolve('user');
                  }
                } catch (err) {
                  clearTimeout(timeoutId);
                  logError('Role fetch exception:', err);
                  resolve('user');
                }
              })();
            });
          };

          // タイムアウト付きでロールを取得
          role = await fetchRoleWithTimeout();

        } catch (error) {
          logError('Exception in role fetch:', error);
          role = 'user';
        }
      }

      const user: User = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        displayName: displayName,
        role: role,
        created_at: supabaseUser.created_at || new Date().toISOString()
      };

      // ロールをローカルストレージに保存
      if (role !== 'user') {
        localStorage.setItem(`user_role_${supabaseUser.id}`, role);
      }

      return user;
    } catch (error) {
      logError('Error in createUserFromSupabaseUser:', error);
      throw error; // エラーを上位に投げる
    }
  };

  // ローディング状態を安全にセット
  const setLoadingState = (isLoading: boolean) => {
    setLoading(isLoading);
  };

  // ユーザー状態を安全にセット
  const setUserState = (user: User | null) => {
    setCurrentUser(user);
    setLoadingState(false); // ユーザー状態セット時は必ずローディング解除
  };

  async function signup(
    email: string,
    password: string,
    displayName?: string
  ): Promise<User> {
    try {
      setError(null);
      setLoadingState(true);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName || '',
            name: displayName || '',
          },
        },
      });

      if (error) throw error;
      if (!data.user) throw new Error('ユーザーの作成に失敗しました');

      const user = await createUserFromSupabaseUser(data.user);
      setUserState(user);
      return user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'サインアップに失敗しました';
      logError('Signup failed:', errorMessage);
      setError(errorMessage);
      setLoadingState(false);
      throw new Error(errorMessage);
    }
  }

  async function login(email: string, password: string): Promise<User> {
    try {
      setError(null);
      setLoadingState(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logError('Login error:', error);
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('メールアドレスまたはパスワードが正しくありません。');
        }
        throw error;
      }

      if (!data.user) {
        throw new Error('ログインに失敗しました：ユーザーデータが取得できません');
      }

      const user = await createUserFromSupabaseUser(data.user);

      // 即座に状態を更新
      setUserState(user);

      return user;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'ログインに失敗しました';
      logError('Login failed:', errorMessage);
      setError(errorMessage);
      setLoadingState(false);
      throw new Error(errorMessage);
    }
  }

  async function loginWithGoogle(): Promise<void> {
    try {
      setError(null);
      setLoadingState(true);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Google認証に失敗しました';
      logError('Google login failed:', errorMessage);
      setError(errorMessage);
      setLoadingState(false);
      throw new Error(errorMessage);
    }
  }

  async function logout() {
    try {
      setError(null);

      // ローカルストレージからロールをクリア
      if (currentUser) {
        localStorage.removeItem(`user_role_${currentUser.id}`);
      }

      // 即座に状態をクリア
      setUserState(null);

      // その後、Supabaseからサインアウト
      const { error } = await supabase.auth.signOut();
      if (error) {
        logError('Supabase logout error (continuing anyway):', error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'ログアウトに失敗しました';
      logError('Logout exception:', errorMessage);
      setError(errorMessage);
      // エラーでも確実に状態をクリア
      setUserState(null);
    }
  }

  async function resetPassword(email: string) {
    try {
      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/reset-password-confirmation`,
      });
      if (error) throw error;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'パスワードリセットに失敗しました';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function updatePassword(newPassword: string) {
    try {
      setError(null);
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'パスワード更新に失敗しました';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  // 緊急時のローディング解除（5秒後）
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !isInitialized) {
        logError('EMERGENCY: Forcing loading to false after 5 seconds');
        setLoadingState(false);
        setIsInitialized(true);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, [loading, isInitialized]);

  // 認証状態を監視（シンプル化）
  useEffect(() => {
    let mounted = true;
    let sessionChecked = false;

    // 初期セッションを取得
    const getInitialSession = async () => {
      if (!mounted || sessionChecked) return;
      sessionChecked = true;

      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!mounted) return;

        if (error) {
          logError('Initial session error:', error);
          setUserState(null);
          setIsInitialized(true);
          return;
        }

        if (session?.user) {
          // 初期セッションでも既存のロールがあれば保持
          const user = await createUserFromSupabaseUser(session.user, currentUser ? true : false);
          setUserState(user);
        } else {
          setUserState(null);
        }

        setIsInitialized(true);
      } catch (error) {
        logError('Get initial session exception:', error);
        if (mounted) {
          setUserState(null);
          setIsInitialized(true);
        }
      }
    };

    // 認証状態変更リスナー
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      // 初期セッションイベントはスキップ
      if (event === 'INITIAL_SESSION') {
        if (!sessionChecked) {
          await getInitialSession();
        }
        return;
      }

      try {
        switch (event) {
          case 'SIGNED_IN':
            if (session?.user) {
              const user = await createUserFromSupabaseUser(session.user);
              setUserState(user);
            }
            break;

          case 'SIGNED_OUT':
            setUserState(null);
            break;

          case 'TOKEN_REFRESHED':
          case 'USER_UPDATED':
            if (session?.user) {
              // トークンリフレッシュやユーザー更新時はロールを保持
              const user = await createUserFromSupabaseUser(session.user, currentUser ? true : false);
              setUserState(user);
            }
            break;
        }
      } catch (error) {
        logError('Error processing auth state change:', error);
        if (event === 'SIGNED_OUT') {
          setUserState(null); // ログアウトは確実に実行
        }
      }
    });

    // 初期セッションチェックを開始
    getInitialSession();

    // クリーンアップ
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // 空の依存配列

  const value = {
    currentUser,
    loading,
    error,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    updatePassword,
    updateEmail: async () => { throw new Error('Not implemented'); },
    isUserAdmin: () => currentUser?.role === 'admin' || currentUser?.role === 'system_admin',
    setUserRole: async () => { throw new Error('Not implemented'); }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}